import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { genres } from '@/mocks/genres';

const navItems = [
  { label: '首页', to: '/' },
  { label: '资讯', to: '/news' },
  { label: '热门电影', to: '/movies/popular' },
  { label: '关于我们', to: '/about' },
];

export default function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setDrawerOpen(false);
    setTypeOpen(false);
  }, [location.pathname]);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : '/search');
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative px-3 py-2 text-sm font-medium transition-colors ${
      isActive ? 'text-accent-400' : 'text-background-100 hover:text-accent-400'
    }`;

  return (
    <header className="sticky top-0 z-50 w-full bg-primary-800">
      <nav className="w-full px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-lg text-background-50">
              <i className="ri-clapperboard-line" />
            </span>
            <span className="font-heading text-lg font-bold tracking-wide text-background-50">
              草民电影
            </span>
          </Link>

          <div className="hidden items-center lg:flex">
            {navItems.slice(0, 3).map((item) => (
              <NavLink key={item.to} to={item.to} className={linkClass}>
                {item.label}
              </NavLink>
            ))}

            <div
              className="relative"
              onMouseEnter={() => setTypeOpen(true)}
              onMouseLeave={() => setTypeOpen(false)}
            >
              <button
                type="button"
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-background-100 transition-colors hover:text-accent-400"
              >
                类型 <i className="ri-arrow-down-s-line" />
              </button>
              {typeOpen && (
                <div className="absolute left-0 top-full grid w-64 grid-cols-2 gap-1 rounded-lg border border-background-200 bg-background-50 p-2 shadow-sm">
                  {genres.map((genre) => (
                    <Link
                      key={genre.key}
                      to={`/movies/category/${genre.key}`}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-800 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      <i className={`${genre.icon} text-base text-accent-500`} />
                      {genre.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <NavLink to="/about" className={linkClass}>
              关于我们
            </NavLink>
          </div>

          <div className="hidden items-center gap-3 lg:flex">
            <form onSubmit={handleSearch} className="relative">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-background-300" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片或资讯"
                className="h-9 w-48 rounded-full border border-primary-600 bg-primary-700/60 pl-9 pr-3 text-sm text-background-50 placeholder:text-background-400 focus:border-accent-400 focus:outline-none"
              />
            </form>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <Link
              to="/search"
              aria-label="搜索"
              className="flex h-9 w-9 items-center justify-center rounded-md text-background-50"
            >
              <i className="ri-search-line text-xl" />
            </Link>
            <button
              type="button"
              aria-label="菜单"
              onClick={() => setDrawerOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-md text-background-50"
            >
              <i className="ri-menu-line text-2xl" />
            </button>
          </div>
        </div>
      </nav>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="absolute right-0 top-0 h-full w-72 max-w-[80%] overflow-y-auto bg-background-50 p-5">
            <div className="mb-6 flex items-center justify-between">
              <span className="font-heading text-base font-bold text-primary-800">草民电影</span>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-700"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>

            <div className="flex flex-col gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2.5 text-sm font-medium ${
                      isActive ? 'bg-primary-50 text-primary-700' : 'text-foreground-800'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>

            <p className="mb-2 mt-5 px-3 text-xs font-semibold uppercase tracking-wide text-foreground-400">
              电影类型
            </p>
            <div className="grid grid-cols-2 gap-1">
              {genres.map((genre) => (
                <Link
                  key={genre.key}
                  to={`/movies/category/${genre.key}`}
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700"
                >
                  <i className={`${genre.icon} text-base text-accent-500`} />
                  {genre.name}
                </Link>
              ))}
            </div>

            <Link
              to="/contact"
              className="mt-6 flex items-center justify-center rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-background-50"
            >
              联系我们
            </Link>
          </aside>
        </div>
      )}
    </header>
  );
}